import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as BlogAPI from 'api/Blog';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { unwrap, unwrapList } from 'utils/notify';

// ───────────────────────────────────────────────────────────────────────────
// Blog store — mirrors the public /api/blog/ read-only API.
//
// The spec documents bare DRF shapes (paginated envelope for the list, plain
// arrays for categories/tags, the object itself for the detail), but the rest
// of this backend wraps payloads in `{ data, error, message }`. To be safe we
// route every response through `unwrap` / `unwrapList`, which transparently
// accept BOTH shapes — so the pages render regardless of which the server
// actually returns.
// ───────────────────────────────────────────────────────────────────────────

// A 429 means we hit the `blog_read` throttle (120/min per IP) — surface a
// friendly "slow down" rather than a generic failure.
const errorMessage = (error) => {
  if (error?.response?.status === 429) return 'Too many requests — please slow down and try again in a moment.';
  return error?.response?.data?.message || error?.message || 'Something went wrong';
};

const notifyError = (error) => {
  dispatch(
    openSnackbar({
      open: true,
      message: errorMessage(error),
      variant: 'alert',
      alert: { color: 'error' },
      close: false
    })
  );
};

const notifySuccess = (message) => {
  dispatch(
    openSnackbar({
      open: true,
      message,
      variant: 'alert',
      alert: { color: 'success' },
      close: false
    })
  );
};

// Empty = no explicit ordering sent → server applies its own default
// (featured first). Keeps the Sort dropdown in its placeholder state by default.
const DEFAULT_ORDERING = '';

const initialState = {
  posts: {
    data: [],
    pagination: { pageSize: 12, currentPage: 1, count: 0, next: null, previous: null },
    filters: { search: '', category: '', tag: '', status: '', ordering: DEFAULT_ORDERING },
    loading: false,
    // Separate flag for create/update/delete so a mutation spinner doesn't
    // blank out the list.
    mutating: false,
    error: null
  },
  post: {
    data: null,
    loading: false,
    // 404 (unknown / not-live slug) is a first-class state, not just an error.
    notFound: false,
    error: null
  },
  categories: { data: [], loading: false, mutating: false },
  tags: { data: [], loading: false, mutating: false }
};

// ── thunks ──────────────────────────────────────────────────────────────────

const getBlogPosts = createAsyncThunk('blog/getBlogPosts', async ({ params, signal } = {}, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.getBlogPosts(params, signal);
    // unwrapList → { count, results }; keep next/previous from the raw payload.
    const payload = unwrap(res) || {};
    const list = unwrapList(res);
    return { ...list, next: payload.next || null, previous: payload.previous || null };
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const getBlogPost = createAsyncThunk('blog/getBlogPost', async ({ slug, signal } = {}, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.getBlogPost(slug, signal);
    return unwrap(res);
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 404) notifyError(error);
    return rejectWithValue({ status, message: errorMessage(error) });
  }
});

const getBlogCategories = createAsyncThunk('blog/getBlogCategories', async (signal, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.getBlogCategories(signal);
    return unwrap(res);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

const getBlogTags = createAsyncThunk('blog/getBlogTags', async (signal, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.getBlogTags(signal);
    return unwrap(res);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

// ── mutations (authoring) ─────────────────────────────────────────────────

const createBlogPost = createAsyncThunk('blog/createBlogPost', async (payload, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.createBlogPost(payload);
    notifySuccess('Post created');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const updateBlogPost = createAsyncThunk('blog/updateBlogPost', async ({ slug, payload }, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.updateBlogPost(slug, payload);
    notifySuccess('Post updated');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const deleteBlogPost = createAsyncThunk('blog/deleteBlogPost', async (slug, { rejectWithValue }) => {
  try {
    await BlogAPI.deleteBlogPost(slug);
    notifySuccess('Post deleted');
    return slug;
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

// Lifecycle actions — publish / unpublish / archive. Each returns the updated
// post (detail shape); we notify and let the reducer sync the open detail view.
const runLifecycle = (name, apiFn, successMsg) =>
  createAsyncThunk(`blog/${name}`, async (slug, { rejectWithValue }) => {
    try {
      const res = await apiFn(slug);
      notifySuccess(successMsg);
      return unwrap(res);
    } catch (error) {
      notifyError(error);
      return rejectWithValue(errorMessage(error));
    }
  });

const publishBlogPost = runLifecycle('publishBlogPost', BlogAPI.publishBlogPost, 'Post published');
const unpublishBlogPost = runLifecycle('unpublishBlogPost', BlogAPI.unpublishBlogPost, 'Post unpublished');
const archiveBlogPost = runLifecycle('archiveBlogPost', BlogAPI.archiveBlogPost, 'Post archived');

// ── taxonomy mutations (categories & tags) ────────────────────────────────

const createBlogCategory = createAsyncThunk('blog/createBlogCategory', async (payload, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.createBlogCategory(payload);
    notifySuccess('Category created');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const updateBlogCategory = createAsyncThunk('blog/updateBlogCategory', async ({ slug, payload }, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.updateBlogCategory(slug, payload);
    notifySuccess('Category updated');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const deleteBlogCategory = createAsyncThunk('blog/deleteBlogCategory', async (slug, { rejectWithValue }) => {
  try {
    await BlogAPI.deleteBlogCategory(slug);
    notifySuccess('Category deleted');
    return slug;
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const createBlogTag = createAsyncThunk('blog/createBlogTag', async (payload, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.createBlogTag(payload);
    notifySuccess('Tag created');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const updateBlogTag = createAsyncThunk('blog/updateBlogTag', async ({ slug, payload }, { rejectWithValue }) => {
  try {
    const res = await BlogAPI.updateBlogTag(slug, payload);
    notifySuccess('Tag updated');
    return unwrap(res);
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

const deleteBlogTag = createAsyncThunk('blog/deleteBlogTag', async (slug, { rejectWithValue }) => {
  try {
    await BlogAPI.deleteBlogTag(slug);
    notifySuccess('Tag deleted');
    return slug;
  } catch (error) {
    notifyError(error);
    return rejectWithValue(errorMessage(error));
  }
});

// ── slice ─────────────────────────────────────────────────────────────────

const BlogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    resetState: () => initialState,
    setBlogCurrentPage: (state, action) => {
      state.posts.pagination.currentPage = action.payload;
    },
    setBlogPageSize: (state, action) => {
      state.posts.pagination.pageSize = action.payload;
      state.posts.pagination.currentPage = 1;
    },
    setBlogFilter: (state, action) => {
      // { key, value } — reset to page 1 whenever a filter changes.
      const { key, value } = action.payload;
      state.posts.filters[key] = value;
      state.posts.pagination.currentPage = 1;
    },
    clearBlogFilters: (state) => {
      state.posts.filters = { search: '', category: '', tag: '', status: '', ordering: DEFAULT_ORDERING };
      state.posts.pagination.currentPage = 1;
    },
    clearBlogPost: (state) => {
      state.post = { data: null, loading: false, notFound: false, error: null };
    }
  },
  extraReducers: {
    // list
    [getBlogPosts.pending]: (state) => {
      state.posts.loading = true;
      state.posts.error = null;
    },
    [getBlogPosts.fulfilled]: (state, action) => {
      state.posts.loading = false;
      state.posts.data = action.payload?.results || [];
      state.posts.pagination.count = action.payload?.count || 0;
      state.posts.pagination.next = action.payload?.next || null;
      state.posts.pagination.previous = action.payload?.previous || null;
    },
    [getBlogPosts.rejected]: (state, action) => {
      state.posts.loading = false;
      state.posts.error = action.payload || action.error?.message;
    },

    // detail
    [getBlogPost.pending]: (state) => {
      state.post.loading = true;
      state.post.notFound = false;
      state.post.error = null;
      state.post.data = null;
    },
    [getBlogPost.fulfilled]: (state, action) => {
      state.post.loading = false;
      state.post.data = action.payload;
    },
    [getBlogPost.rejected]: (state, action) => {
      state.post.loading = false;
      state.post.notFound = action.payload?.status === 404;
      state.post.error = action.payload?.message || action.error?.message;
    },

    // categories
    [getBlogCategories.pending]: (state) => {
      state.categories.loading = true;
    },
    [getBlogCategories.fulfilled]: (state, action) => {
      state.categories.loading = false;
      state.categories.data = Array.isArray(action.payload) ? action.payload : action.payload?.results || [];
    },
    [getBlogCategories.rejected]: (state) => {
      state.categories.loading = false;
    },

    // tags
    [getBlogTags.pending]: (state) => {
      state.tags.loading = true;
    },
    [getBlogTags.fulfilled]: (state, action) => {
      state.tags.loading = false;
      state.tags.data = Array.isArray(action.payload) ? action.payload : action.payload?.results || [];
    },
    [getBlogTags.rejected]: (state) => {
      state.tags.loading = false;
    },

    // mutations — the owning component re-fetches the list on success, so the
    // reducers only track the shared `mutating` flag (+ patch delete locally).
    [createBlogPost.pending]: (state) => {
      state.posts.mutating = true;
    },
    [createBlogPost.fulfilled]: (state) => {
      state.posts.mutating = false;
    },
    [createBlogPost.rejected]: (state) => {
      state.posts.mutating = false;
    },
    [updateBlogPost.pending]: (state) => {
      state.posts.mutating = true;
    },
    [updateBlogPost.fulfilled]: (state, action) => {
      state.posts.mutating = false;
      // Keep the open detail view in sync if it's the same post.
      if (action.payload && state.post.data?.slug === action.payload.slug) {
        state.post.data = { ...state.post.data, ...action.payload };
      }
    },
    [updateBlogPost.rejected]: (state) => {
      state.posts.mutating = false;
    },
    [deleteBlogPost.pending]: (state) => {
      state.posts.mutating = true;
    },
    [deleteBlogPost.fulfilled]: (state, action) => {
      state.posts.mutating = false;
      state.posts.data = state.posts.data.filter((p) => p.slug !== action.payload);
      state.posts.pagination.count = Math.max(0, state.posts.pagination.count - 1);
    },
    [deleteBlogPost.rejected]: (state) => {
      state.posts.mutating = false;
    },

    // lifecycle (publish / unpublish / archive) — sync the open detail + list row.
    ...[publishBlogPost, unpublishBlogPost, archiveBlogPost].reduce((acc, thunk) => {
      acc[thunk.pending] = (state) => {
        state.posts.mutating = true;
      };
      acc[thunk.fulfilled] = (state, action) => {
        state.posts.mutating = false;
        const updated = action.payload;
        if (!updated) return;
        if (state.post.data?.slug === updated.slug) state.post.data = { ...state.post.data, ...updated };
        state.posts.data = state.posts.data.map((p) => (p.slug === updated.slug ? { ...p, ...updated } : p));
      };
      acc[thunk.rejected] = (state) => {
        state.posts.mutating = false;
      };
      return acc;
    }, {}),

    // category mutations — patch the list in place so the UI updates instantly.
    [createBlogCategory.pending]: (state) => {
      state.categories.mutating = true;
    },
    [createBlogCategory.fulfilled]: (state, action) => {
      state.categories.mutating = false;
      if (action.payload) state.categories.data = [...state.categories.data, action.payload];
    },
    [createBlogCategory.rejected]: (state) => {
      state.categories.mutating = false;
    },
    [updateBlogCategory.pending]: (state) => {
      state.categories.mutating = true;
    },
    [updateBlogCategory.fulfilled]: (state, action) => {
      state.categories.mutating = false;
      if (action.payload) state.categories.data = state.categories.data.map((c) => (c.id === action.payload.id ? action.payload : c));
    },
    [updateBlogCategory.rejected]: (state) => {
      state.categories.mutating = false;
    },
    [deleteBlogCategory.pending]: (state) => {
      state.categories.mutating = true;
    },
    [deleteBlogCategory.fulfilled]: (state, action) => {
      state.categories.mutating = false;
      state.categories.data = state.categories.data.filter((c) => c.slug !== action.payload);
    },
    [deleteBlogCategory.rejected]: (state) => {
      state.categories.mutating = false;
    },

    // tag mutations
    [createBlogTag.pending]: (state) => {
      state.tags.mutating = true;
    },
    [createBlogTag.fulfilled]: (state, action) => {
      state.tags.mutating = false;
      if (action.payload) state.tags.data = [...state.tags.data, action.payload];
    },
    [createBlogTag.rejected]: (state) => {
      state.tags.mutating = false;
    },
    [updateBlogTag.pending]: (state) => {
      state.tags.mutating = true;
    },
    [updateBlogTag.fulfilled]: (state, action) => {
      state.tags.mutating = false;
      if (action.payload) state.tags.data = state.tags.data.map((t) => (t.id === action.payload.id ? action.payload : t));
    },
    [updateBlogTag.rejected]: (state) => {
      state.tags.mutating = false;
    },
    [deleteBlogTag.pending]: (state) => {
      state.tags.mutating = true;
    },
    [deleteBlogTag.fulfilled]: (state, action) => {
      state.tags.mutating = false;
      state.tags.data = state.tags.data.filter((t) => t.slug !== action.payload);
    },
    [deleteBlogTag.rejected]: (state) => {
      state.tags.mutating = false;
    }
  }
});

const { resetState, setBlogCurrentPage, setBlogPageSize, setBlogFilter, clearBlogFilters, clearBlogPost } = BlogSlice.actions;

export default BlogSlice.reducer;

export {
  resetState,
  setBlogCurrentPage,
  setBlogPageSize,
  setBlogFilter,
  clearBlogFilters,
  clearBlogPost,
  getBlogPosts,
  getBlogPost,
  getBlogCategories,
  getBlogTags,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  publishBlogPost,
  unpublishBlogPost,
  archiveBlogPost,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  createBlogTag,
  updateBlogTag,
  deleteBlogTag
};
